<template>
  <BaseModal :modal-content-class="['modal-achievement', {completed: !isLocked}]">
    <div class="banner_top">
      <BaseImage :src="achievementImage" alt="Achievement" />
    </div>

    <div class="content">
      <div class="ach_modal_center">
        <BaseImage :src="statusImage" alt="Status" />
      </div>

      <h1>{{ $_translations.value[achievementName] }}</h1>

      <div class="unlocked_txt">
        <template v-if="isLocked">
          <p>
            {{ $_translations.value[lockedAchievementDescription.pt1] }}<br>
            {{ $_translations.value[lockedAchievementDescription.pt2] }}
            <span class="yellow_bold">{{ total }}</span>
            {{ $_translations.value[lockedAchievementDescription.pt3] }}
          </p>
        </template>

        <template v-else>
          <p>
            {{ $_translations.value.modal_achievement_unlocked_description_pt1 }} <br>
            {{ $_translations.value.modal_achievement_unlocked_description_pt2 }}
          </p>
        </template>
      </div>

      <div class="flex-row">
        <BaseProgressBar :value="percentageCompleted" />

        <p class="progress_numb">{{ totalCompleted }} / {{ total }}</p>
      </div>

      <RewardBoxItem
        v-if="isLocked"
        :prize-value="prizeValue"
        :prize-type="prizeType"
      />
    </div>
  </BaseModal>
</template>

<script setup>
import { computed } from 'vue';
import BaseModal from '@/components/modal/BaseModal.vue';
import BaseImage from '@/components/image/BaseImage.vue';
import BaseProgressBar from '@/components/bar/BaseProgressBar.vue';
import RewardBoxItem from '@/components/item/RewardBoxItem.vue';
import {
  calculateAchievementTotalCompleted,
  getAchievementBadgeImage,
  getAchievementNameTranslationKey,
} from '@/helpers/reward.js';
import { achievementCategoryEnum } from '@/constants/reward.js';
import { lockedImage, unlockedImage } from '@/constants/image.js';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
});

const isLocked = computed(() => !props.data?.details?.completed);

const statusImage = computed(() => isLocked.value ? lockedImage : unlockedImage);

const achievementImage = computed(() => getAchievementBadgeImage(props.data?.achievementName));

const achievementName = computed(() => getAchievementNameTranslationKey(props.data?.achievementName));

const prizeValue = computed(() => props.data?.details?.prizeValue ?? 0);

const prizeType = computed(() => props.data?.details?.prizeType);

const percentageCompleted = computed(() => props.data?.details?.percentage ?? 0);

const total = computed(() => props.data?.details?.threshold ?? 0);

const totalCompleted = computed(() => calculateAchievementTotalCompleted(total.value, percentageCompleted.value));

const lockedAchievementDescription = computed(() => {
  const category = props.data?.__category;

  if (!Object.values(achievementCategoryEnum).includes(category)) {
    return { pt1: '', pt2: '', pt3: '' };
  }

  return {
    pt1: 'modal_achievement_locked_category_all_description_pt1',
    pt2: `modal_achievement_locked_category_${ category }_description_pt2`,
    pt3: `modal_achievement_locked_category_${ category }_description_pt3`,
  };
});
</script>
